(() => {
  const gsap = window.gsap;
  const ScrollTrigger = window.ScrollTrigger;
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const globalMotionLevel = document.body?.dataset.vorlivaMotion || 'premium';

  if (!gsap || !ScrollTrigger || reducedMotion.matches) {
    document.documentElement.classList.add('vorliva-gsap-fallback');
    return;
  }

  gsap.registerPlugin(ScrollTrigger);
  gsap.globalTimeline.timeScale(globalMotionLevel === 'calm' ? 1.18 : globalMotionLevel === 'cinematic' ? 0.88 : 1);
  ScrollTrigger.config({ ignoreMobileResize: true });
  document.documentElement.classList.add('vorliva-gsap-ready');

  const toArray = (scope, selector) => gsap.utils.toArray(selector, scope);
  const sections = [
    '.vdh',
    '.vpl',
    '.vorliva-collection-stage',
    '.vorliva-product-story',
    '.vorliva-motion-gallery',
    '.review-stack',
    '.vorliva-trust-hub'
  ].join(',');

  const createProgress = () => {
    const existing = document.querySelector('.vorliva-page-progress');
    if (existing) return existing.firstElementChild;
    const progress = document.createElement('div');
    progress.className = 'vorliva-page-progress';
    progress.setAttribute('aria-hidden', 'true');
    progress.innerHTML = '<i></i>';
    document.body.appendChild(progress);
    return progress.firstElementChild;
  };

  const splitWords = (element) => {
    if (!element || element.dataset.vorlivaWords === 'ready' || element.children.length) return [];
    const text = element.textContent.replace(/\s+/g, ' ').trim();
    if (!text) return [];
    const fragment = document.createDocumentFragment();
    text.split(' ').forEach((word, index, words) => {
      const span = document.createElement('span');
      span.className = 'vorliva-motion-word';
      span.textContent = word;
      fragment.appendChild(span);
      if (index < words.length - 1) fragment.appendChild(document.createTextNode(' '));
    });
    element.replaceChildren(fragment);
    element.dataset.vorlivaWords = 'ready';
    return toArray(element, '.vorliva-motion-word');
  };

  const enhanceOpening = () => {
    const opening = document.querySelector('.vorliva-opening');
    if (!opening || opening.dataset.vorlivaGsap === 'ready') return;
    opening.dataset.vorlivaGsap = 'ready';
    const brandLetters = toArray(opening, '.vorliva-opening__word span');
    const journey = opening.querySelector('.vorliva-opening__journey');
    const timeline = gsap.timeline({ defaults: { ease: 'power3.out' } });
    timeline.fromTo(toArray(opening, '.vorliva-opening__top > *'), {
      y: -7,
      autoAlpha: 0
    }, {
      y: 0,
      autoAlpha: 1,
      duration: 0.52,
      stagger: 0.055,
      clearProps: 'transform,opacity,visibility'
    });
    if (brandLetters.length) {
      timeline.fromTo(brandLetters, {
        yPercent: 16,
        autoAlpha: 0.28,
        filter: 'blur(2px)'
      }, {
        yPercent: 0,
        autoAlpha: 1,
        filter: 'blur(0px)',
        duration: 0.86,
        stagger: 0.035,
        clearProps: 'transform,opacity,visibility,filter'
      }, '-=0.22');
    }
    timeline.fromTo(toArray(opening, '.vorliva-opening__copy > p, .vorliva-opening__copy > strong'), {
      y: 8,
      autoAlpha: 0
    }, {
      y: 0,
      autoAlpha: 1,
      duration: 0.58,
      stagger: 0.07,
      clearProps: 'transform,opacity,visibility'
    }, '-=0.48');
    if (journey) {
      timeline.fromTo(toArray(journey, '.vorliva-opening__journey-labels span'), {
        y: 5,
        autoAlpha: 0
      }, {
        y: 0,
        autoAlpha: 1,
        duration: 0.42,
        stagger: 0.08,
        clearProps: 'transform,opacity,visibility'
      }, '-=0.32');
    }
  };

  const revealHero = () => {
    const hero = document.querySelector('.vdh');
    if (!hero || hero.dataset.vptInitialized === 'true' || hero.dataset.vorlivaGsap === 'ready') return;
    hero.dataset.vorlivaGsap = 'ready';
    gsap.timeline({ defaults: { ease: 'power3.out' } })
      .fromTo(toArray(hero, '.vdh__headline p'), {
        clipPath: 'inset(0 0 100% 0)',
        autoAlpha: 0.3
      }, {
        clipPath: 'inset(0 0 0% 0)',
        autoAlpha: 1,
        duration: 0.86,
        stagger: 0.08,
        clearProps: 'clipPath,opacity,visibility'
      })
      .fromTo(toArray(hero, '.vdh__story > *'), {
        y: 13,
        autoAlpha: 0
      }, {
        y: 0,
        autoAlpha: 1,
        duration: 0.62,
        stagger: 0.06,
        clearProps: 'transform,opacity,visibility'
      }, '-=0.5')
      .fromTo(toArray(hero, '.vdh__selectors button'), {
        y: 7,
        autoAlpha: 0
      }, {
        y: 0,
        autoAlpha: 1,
        duration: 0.46,
        stagger: 0.04,
        clearProps: 'transform,opacity,visibility'
      }, '-=0.34');
  };

  const setupTactileFeedback = () => {
    if (document.documentElement.dataset.vorlivaTactile === 'ready') return;
    document.documentElement.dataset.vorlivaTactile = 'ready';
    const controls = 'a[href]:not([href="#"]), button:not([disabled]), summary';
    let pressed = null;

    const release = () => {
      if (!pressed) return;
      gsap.to(pressed, {
        '--vorliva-press-scale': 1,
        duration: 0.24,
        ease: 'back.out(2)',
        overwrite: 'auto'
      });
      pressed = null;
    };

    document.addEventListener('pointerdown', (event) => {
      if (event.button !== 0) return;
      pressed = event.target.closest?.(controls);
      if (!pressed) return;
      gsap.to(pressed, {
        '--vorliva-press-scale': 0.985,
        duration: 0.1,
        ease: 'power2.out',
        overwrite: 'auto'
      });
    });
    document.addEventListener('pointerup', release);
    document.addEventListener('pointercancel', release);
    window.addEventListener('blur', release);
  };

  const setupGalleryInteractions = () => {
    document.querySelectorAll('.vorliva-motion-gallery').forEach((gallery) => {
      if (gallery.dataset.vorlivaGallery === 'ready') return;
      gallery.dataset.vorlivaGallery = 'ready';
      const finePointer = window.matchMedia('(hover: hover) and (pointer: fine)');

      gallery.querySelectorAll('.vorliva-motion-gallery__card').forEach((card) => {
        const title = card.querySelector('.vorliva-motion-gallery__meta h3');
        const type = card.querySelector('.vorliva-motion-gallery__meta p');
        const price = card.querySelector('.vorliva-motion-gallery__price');
        const arrow = card.querySelector('.vorliva-motion-gallery__arrow');

        const enter = () => {
          if (!finePointer.matches) return;
          if (title) gsap.to(title, { x: 6, duration: 0.42, ease: 'power3.out', overwrite: 'auto' });
          if (type) gsap.to(type, { x: 6, autoAlpha: 0.78, duration: 0.46, ease: 'power3.out', overwrite: 'auto' });
          if (price) gsap.to(price, { y: -4, duration: 0.42, ease: 'power3.out', overwrite: 'auto' });
          if (arrow) gsap.to(arrow, { rotation: 0, duration: 0.46, ease: 'back.out(1.7)', overwrite: 'auto' });
        };

        const leave = () => {
          if (title) gsap.to(title, { x: 0, duration: 0.48, ease: 'power3.out', overwrite: 'auto' });
          if (type) gsap.to(type, { x: 0, autoAlpha: 0.55, duration: 0.48, ease: 'power3.out', overwrite: 'auto' });
          if (price) gsap.to(price, { y: 0, duration: 0.48, ease: 'power3.out', overwrite: 'auto' });
        };

        card.addEventListener('pointerenter', enter);
        card.addEventListener('pointerleave', leave);
        card.addEventListener('focusin', enter);
        card.addEventListener('focusout', leave);
      });
    });
  };

  const setupAccordions = () => {
    document.querySelectorAll('.vorliva-trust-hub__benefit').forEach((details) => {
      if (details.dataset.vorlivaAccordion === 'ready') return;
      details.dataset.vorlivaAccordion = 'ready';
      details.addEventListener('toggle', () => {
        const content = details.querySelector('p');
        if (!content || !details.open) return;
        gsap.fromTo(content, {
          y: -5,
          autoAlpha: 0.5
        }, {
          y: 0,
          autoAlpha: 1,
          duration: 0.42,
          ease: 'power3.out',
          clearProps: 'transform,opacity,visibility'
        });
      });
    });
  };

  const setupReviewMotion = () => {
    document.querySelectorAll('.review-stack').forEach((root) => {
      if (root.dataset.vorlivaReview === 'ready') return;
      root.dataset.vorlivaReview = 'ready';
      const animateActive = () => {
        const active = root.querySelector('.review-stack__card.is-active');
        if (!active) return;
        gsap.fromTo(toArray(active, '.review-stack__product, .review-stack__review, .review-stack__button'), {
          y: 8,
          autoAlpha: 0.72
        }, {
          y: 0,
          autoAlpha: 1,
          duration: 0.56,
          stagger: 0.045,
          ease: 'power3.out',
          overwrite: 'auto',
          clearProps: 'transform,opacity,visibility'
        });
      };
      const observer = new MutationObserver((records) => {
        if (records.some((record) => record.attributeName === 'class')) animateActive();
      });
      root.querySelectorAll('.review-stack__card').forEach((card) => {
        observer.observe(card, { attributes: true, attributeFilter: ['class'] });
      });
    });
  };

  const buildScrollExperience = (scroller, allowPinning) => {
    const scrollerConfig = scroller === window ? {} : { scroller };
    const progress = createProgress();
    const context = gsap.context(() => {
      ScrollTrigger.create({
        ...scrollerConfig,
        start: 0,
        end: 'max',
        invalidateOnRefresh: true,
        onUpdate: (self) => gsap.set(progress, { scaleX: self.progress })
      });

      document.querySelectorAll(sections).forEach((section) => {
        const heading = section.querySelector('h2');
        if (!heading) return;
        gsap.fromTo(heading, {
          y: 20,
          autoAlpha: 0.46
        }, {
          y: 0,
          autoAlpha: 1,
          duration: 0.88,
          ease: 'power3.out',
          clearProps: 'transform,opacity,visibility',
          scrollTrigger: {
            ...scrollerConfig,
            trigger: heading,
            start: 'top 88%',
            once: true,
            fastScrollEnd: true
          }
        });
      });

      const copySelectors = [
        '.vpl__footer p',
        '.vorliva-collection-stage__description',
        '.vorliva-product-story__description',
        '.vorliva-motion-gallery__header-side > p',
        '.vorliva-trust-hub__lead'
      ].join(',');
      document.querySelectorAll(copySelectors).forEach((copy) => {
        const words = splitWords(copy);
        if (!words.length) return;
        gsap.fromTo(words, {
          autoAlpha: 0.16
        }, {
          autoAlpha: 1,
          stagger: 0.028,
          ease: 'none',
          scrollTrigger: {
            ...scrollerConfig,
            trigger: copy,
            start: 'top 90%',
            end: 'bottom 55%',
            scrub: 0.62
          }
        });
      });

      const images = document.querySelectorAll([
        '.vorliva-collection-stage__media img',
        '.vorliva-product-story__frame img',
        '.review-stack__media img',
        '.vorliva-trust-hub__inline-image img'
      ].join(','));
      images.forEach((image) => {
        const timeline = gsap.timeline({
          scrollTrigger: {
            ...scrollerConfig,
            trigger: image.closest('article, .vorliva-product-story__frame, .vorliva-trust-hub__inline-image') || image,
            start: 'top 94%',
            end: 'bottom 4%',
            scrub: 0.72
          }
        });
        timeline.fromTo(image, {
          scale: 0.92,
          autoAlpha: 0.58
        }, {
          scale: 1,
          autoAlpha: 1,
          ease: 'none',
          duration: 0.68
        }).to(image, {
          scale: 0.985,
          autoAlpha: 0.42,
          ease: 'none',
          duration: 0.32
        });
      });

      document.querySelectorAll('.vorliva-motion-gallery').forEach((gallery) => {
        const track = gallery.querySelector('.vorliva-motion-gallery__track');
        const cards = toArray(gallery, '.vorliva-motion-gallery__card');
        if (!track || !cards.length) return;

        gsap.fromTo(cards, {
          autoAlpha: 0.28,
          filter: 'blur(3px)',
          clipPath: 'inset(10% 0 0 0)'
        }, {
          autoAlpha: 1,
          filter: 'blur(0px)',
          clipPath: 'inset(0% 0 0 0)',
          stagger: 0.075,
          ease: 'none',
          scrollTrigger: {
            ...scrollerConfig,
            trigger: track,
            start: 'top 92%',
            end: 'top 42%',
            scrub: 0.68
          }
        });

        cards.forEach((card, index) => {
          const image = card.querySelector('.vorliva-motion-gallery__image--primary');
          if (!image) return;
          gsap.fromTo(image, {
            scale: 0.88,
            yPercent: 4 + (index % 3) * 2
          }, {
            scale: 1,
            yPercent: 0,
            ease: 'none',
            scrollTrigger: {
              ...scrollerConfig,
              trigger: card,
              start: 'top 96%',
              end: 'bottom 48%',
              scrub: 0.72
            }
          });
        });
      });

      [
        '.vorliva-collection-stage__track',
        '.vorliva-trust-hub__notes-track',
        '.review-stack__product-nav'
      ].forEach((selector) => {
        const track = document.querySelector(selector);
        if (!track) return;
        const cards = toArray(track, ':scope > *');
        if (!cards.length) return;
        gsap.fromTo(cards, {
          autoAlpha: 0.42,
          clipPath: 'inset(8% 0 0 0)'
        }, {
          autoAlpha: 1,
          clipPath: 'inset(0% 0 0 0)',
          stagger: 0.06,
          ease: 'none',
          scrollTrigger: {
            ...scrollerConfig,
            trigger: track,
            start: 'top 92%',
            end: 'top 52%',
            scrub: 0.58
          }
        });
      });

      if (allowPinning) {
        const trust = document.querySelector('.vorliva-trust-hub__editorial');
        const trustCopy = trust?.querySelector('.vorliva-trust-hub__copy');
        if (trust && trustCopy && trust.offsetHeight > window.innerHeight * 1.1) {
          ScrollTrigger.create({
            ...scrollerConfig,
            trigger: trust,
            start: 'top 16%',
            end: 'bottom 58%',
            pin: trustCopy,
            pinSpacing: true,
            invalidateOnRefresh: true
          });
        }
      }
    }, document.body);

    window.requestAnimationFrame(() => ScrollTrigger.refresh());
    return () => context.revert();
  };

  enhanceOpening();
  setupTactileFeedback();
  setupGalleryInteractions();
  setupAccordions();
  setupReviewMotion();

  if (document.documentElement.dataset.vorlivaIntro === 'complete' || !document.querySelector('.vorliva-opening')) {
    window.requestAnimationFrame(revealHero);
  } else {
    document.addEventListener('vorliva:intro-complete', revealHero, { once: true });
    window.setTimeout(revealHero, 3000);
  }

  const media = gsap.matchMedia();
  media.add('(min-width: 990px)', () => buildScrollExperience(document.querySelector('.page-wrapper') || window, true));
  media.add('(max-width: 989px)', () => buildScrollExperience(window, false));

  document.addEventListener('vorliva:case-change', () => {
    const active = document.querySelector('.vdh__case.is-active');
    if (!active) return;
    gsap.fromTo(toArray(active, '.vdh__case-image, .vdh__purchase > *'), {
      y: 7,
      autoAlpha: 0.76
    }, {
      y: 0,
      autoAlpha: 1,
      duration: 0.58,
      stagger: 0.04,
      ease: 'power3.out',
      overwrite: 'auto',
      clearProps: 'transform,opacity,visibility'
    });
  });

  document.addEventListener('vorliva:feature-change', () => {
    const active = document.querySelector('.vpl__visual.is-active');
    if (!active) return;
    gsap.fromTo(toArray(active, '.vpl__visual-image, .vpl__hotspot'), {
      y: 7,
      autoAlpha: 0.74
    }, {
      y: 0,
      autoAlpha: 1,
      duration: 0.6,
      stagger: 0.045,
      ease: 'power3.out',
      overwrite: 'auto',
      clearProps: 'transform,opacity,visibility'
    });
  });

  document.addEventListener('shopify:section:load', () => {
    setupGalleryInteractions();
    setupAccordions();
    setupReviewMotion();
    window.requestAnimationFrame(() => ScrollTrigger.refresh());
  });
  document.addEventListener('shopify:section:unload', () => window.requestAnimationFrame(() => ScrollTrigger.refresh()));
  document.addEventListener('vorliva:intro-complete', () => window.setTimeout(() => ScrollTrigger.refresh(), 80));
  window.addEventListener('load', () => ScrollTrigger.refresh(), { once: true });
})();
